import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getOrCreateTimesheet,
  getMeRbac,
  submitTimesheet,
  reviewTimesheet,
  listProjects,
  listTasks,
} from "@/lib/backend";
import type { Timesheet, TimeEntry } from "@/lib/backend";
import type { ProjectSummary, Task, MeRbacPayload } from "@/lib/types";
import { toast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { demoStorageKey, isDemoModeScoped } from "@/lib/demoMode";
import WeekNavigator from "./WeekNavigator";
import TimesheetGrid from "./TimesheetGrid";

interface TimesheetPanelProps {
  tabId?: string;
  user?: { id?: string; email?: string };
}

interface TimesheetDemoSnapshot {
  timesheet: Timesheet;
  entries: TimeEntry[];
}

function computeWeekStart(date: Date, weekStartDay: number): Date {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const current = d.getUTCDay();
  const diff = (current - weekStartDay + 7) % 7;
  d.setUTCDate(d.getUTCDate() - diff);
  return d;
}

function formatDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function statusLabel(status: Timesheet["status"]): string {
  switch (status) {
    case "draft": return "Draft";
    case "submitted": return "Submitted";
    case "approved": return "Approved";
    case "rejected": return "Returned";
  }
}

function statusVariant(status: Timesheet["status"]): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "draft": return "secondary";
    case "submitted": return "default";
    case "approved": return "outline";
    case "rejected": return "destructive";
  }
}

export default function TimesheetPanel({ tabId: _tabId, user }: TimesheetPanelProps) {
  const [weekStartDay, setWeekStartDay] = useState(1); // 1 = Monday default
  const [currentPeriodStart, setCurrentPeriodStart] = useState<string>(() => {
    return formatDateKey(computeWeekStart(new Date(), 1));
  });

  const [timesheet, setTimesheet] = useState<Timesheet | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rbac, setRbac] = useState<MeRbacPayload | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<"approve" | "reject" | null>(null);
  const [rejectionNote, setRejectionNote] = useState("");
  const [reviewing, setReviewing] = useState(false);

  const isDemo = useMemo(
    () => isDemoModeScoped({ userId: user?.id, email: user?.email }),
    [user?.id, user?.email],
  );

  const getDemoKey = useCallback(
    (periodStart: string) => demoStorageKey("timesheets", periodStart),
    [],
  );

  const persistDemoSnapshot = useCallback(
    (periodStart: string, ts: Timesheet, ents: TimeEntry[]) => {
      const key = getDemoKey(periodStart);
      if (!key) return;
      try {
        const snapshot: TimesheetDemoSnapshot = { timesheet: ts, entries: ents };
        window.localStorage.setItem(key, JSON.stringify(snapshot));
      } catch {
        // ignore storage failures
      }
    },
    [getDemoKey],
  );

  const readDemoSnapshot = useCallback(
    (periodStart: string): TimesheetDemoSnapshot | null => {
      const key = getDemoKey(periodStart);
      if (!key) return null;
      try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" && parsed.timesheet ? (parsed as TimesheetDemoSnapshot) : null;
      } catch {
        return null;
      }
    },
    [getDemoKey],
  );

  const makeDemoTimesheet = useCallback((periodStart: string): Timesheet => {
    const start = new Date(periodStart + "T00:00:00Z");
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    const now = new Date().toISOString();
    return {
      id: `demo-ts-${periodStart}`,
      user_id: user?.id ?? "demo",
      company_id: "demo",
      period_start: periodStart,
      period_end: end.toISOString().slice(0, 10),
      status: "draft",
      submitted_at: null,
      reviewed_by: null,
      reviewed_at: null,
      rejection_note: null,
      created_at: now,
      updated_at: now,
      entries: [],
    };
  }, [user?.id]);

  const canReview = !!(rbac?.permissions?.includes("timesheets:review"));

  // Load RBAC on mount
  useEffect(() => {
    getMeRbac().then((data) => {
      if (data?.rbac) setRbac(data.rbac);
    }).catch(() => {});
  }, []);

  // Load projects and tasks for picker
  useEffect(() => {
    listProjects().then(setProjects).catch(() => {});
    listTasks().then(setAllTasks).catch(() => {});
  }, []);

  const loadTimesheet = useCallback(async (periodStart: string) => {
    setLoading(true);
    setError(null);
    try {
      if (isDemo) {
        const snap = readDemoSnapshot(periodStart);
        if (snap) {
          setTimesheet(snap.timesheet);
          setEntries(snap.entries);
        } else {
          const demoTs = makeDemoTimesheet(periodStart);
          setTimesheet(demoTs);
          setEntries([]);
          persistDemoSnapshot(periodStart, demoTs, []);
        }
      } else {
        const ts = await getOrCreateTimesheet(periodStart);
        setTimesheet(ts);
        setEntries(ts.entries ?? []);
      }
    } catch (e: any) {
      setError(e?.message ?? "Failed to load timesheet");
    } finally {
      setLoading(false);
    }
  }, [isDemo, readDemoSnapshot, makeDemoTimesheet, persistDemoSnapshot]);

  useEffect(() => {
    loadTimesheet(currentPeriodStart);
  }, [currentPeriodStart, loadTimesheet]);

  const handlePeriodChange = useCallback((periodStart: string) => {
    setCurrentPeriodStart(periodStart);
  }, []);

  const handleEntriesChange = useCallback((updated: TimeEntry[]) => {
    setEntries(updated);
    if (isDemo && timesheet) {
      persistDemoSnapshot(currentPeriodStart, timesheet, updated);
    }
  }, [isDemo, timesheet, currentPeriodStart, persistDemoSnapshot]);

  const handleSubmit = async () => {
    if (!timesheet) return;
    setSubmitting(true);
    try {
      if (isDemo) {
        const updated: Timesheet = { ...timesheet, status: "submitted", submitted_at: new Date().toISOString() };
        setTimesheet(updated);
        persistDemoSnapshot(currentPeriodStart, updated, entries);
        toast({ title: "Timesheet submitted", description: "Your timesheet has been submitted for review." });
        return;
      }
      const updated = await submitTimesheet(timesheet.id);
      setTimesheet(updated);
      toast({ title: "Timesheet submitted", description: "Your timesheet has been submitted for review." });
    } catch (e: any) {
      toast({ title: "Submit failed", description: e?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReviewConfirm = async () => {
    if (!timesheet || !reviewAction) return;
    setReviewing(true);
    try {
      if (isDemo) {
        const now = new Date().toISOString();
        const updated: Timesheet = {
          ...timesheet,
          status: reviewAction === "approve" ? "approved" : "rejected",
          reviewed_by: user?.id ?? "demo",
          reviewed_at: now,
          rejection_note: reviewAction === "reject" ? rejectionNote : null,
        };
        setTimesheet(updated);
        persistDemoSnapshot(currentPeriodStart, updated, entries);
        setReviewDialogOpen(false);
        setRejectionNote("");
        toast({
          title: reviewAction === "approve" ? "Timesheet approved" : "Timesheet returned",
          description: reviewAction === "approve"
            ? "The timesheet has been approved."
            : "The timesheet has been returned for corrections.",
        });
        return;
      }
      const updated = await reviewTimesheet(
        timesheet.id,
        reviewAction,
        reviewAction === "reject" ? rejectionNote : undefined,
      );
      setTimesheet(updated);
      setReviewDialogOpen(false);
      setRejectionNote("");
      toast({
        title: reviewAction === "approve" ? "Timesheet approved" : "Timesheet returned",
        description: reviewAction === "approve"
          ? "The timesheet has been approved."
          : "The timesheet has been returned for corrections.",
      });
    } catch (e: any) {
      toast({ title: "Review failed", description: e?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setReviewing(false);
    }
  };

  const totalHours = entries.reduce((sum, e) => sum + Number(e.hours), 0);

  return (
    <div className="flex flex-col h-full bg-black/60 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-white">Timesheet</h2>
          {timesheet && (
            <Badge variant={statusVariant(timesheet.status)} className="text-xs">
              {statusLabel(timesheet.status)}
            </Badge>
          )}
          {timesheet?.status === "rejected" && timesheet.rejection_note && (
            <span className="text-xs text-red-400 truncate max-w-xs" title={timesheet.rejection_note}>
              Note: {timesheet.rejection_note}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {timesheet?.status === "draft" && (
            <Button
              size="sm"
              variant="default"
              disabled={submitting || entries.length === 0}
              onClick={handleSubmit}
              className="text-xs"
            >
              {submitting ? "Submitting…" : "Submit for Review"}
            </Button>
          )}
          {canReview && timesheet?.status === "submitted" && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="text-xs text-green-400 border-green-400/40 hover:bg-green-400/10"
                onClick={() => { setReviewAction("approve"); setReviewDialogOpen(true); }}
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs text-red-400 border-red-400/40 hover:bg-red-400/10"
                onClick={() => { setReviewAction("reject"); setReviewDialogOpen(true); }}
              >
                Return
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Week Navigator */}
      <div className="px-4 py-2 border-b border-white/10 shrink-0">
        <WeekNavigator
          currentPeriodStart={currentPeriodStart}
          weekStartDay={weekStartDay}
          onPeriodChange={handlePeriodChange}
        />
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto">
        {loading && (
          <div className="flex items-center justify-center h-32 text-sm text-white/50">
            Loading timesheet…
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-32 text-sm text-red-400">
            {error}
          </div>
        )}
        {!loading && !error && timesheet && (
          <TimesheetGrid
            timesheet={timesheet}
            entries={entries}
            projects={projects}
            allTasks={allTasks}
            periodStart={currentPeriodStart}
            weekStartDay={weekStartDay}
            onEntriesChange={handleEntriesChange}
            isDemo={isDemo}
          />
        )}
      </div>

      {/* Footer */}
      {!loading && !error && timesheet && (
        <div className="px-4 py-2 border-t border-white/10 shrink-0 flex items-center justify-end gap-3">
          <span className="text-xs text-white/50">
            {entries.length} {entries.length === 1 ? "entry" : "entries"}
          </span>
          <span className="text-sm font-semibold text-white">
            {totalHours % 1 === 0 ? totalHours : totalHours.toFixed(2)} hrs total
          </span>
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {reviewAction === "approve" ? "Approve Timesheet" : "Return Timesheet"}
            </DialogTitle>
          </DialogHeader>
          {reviewAction === "reject" && (
            <div className="py-2">
              <label className="text-xs text-white/70 mb-1 block">Reason for returning (required)</label>
              <Textarea
                placeholder="Explain what needs to be corrected…"
                value={rejectionNote}
                onChange={(e) => setRejectionNote(e.target.value)}
                rows={3}
                className="text-sm"
              />
            </div>
          )}
          {reviewAction === "approve" && (
            <p className="text-sm text-white/70 py-2">
              Approve this timesheet? This will mark it as final.
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant={reviewAction === "approve" ? "default" : "destructive"}
              disabled={reviewing || (reviewAction === "reject" && !rejectionNote.trim())}
              onClick={handleReviewConfirm}
            >
              {reviewing ? "Saving…" : reviewAction === "approve" ? "Approve" : "Return"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
